import { useMemoize } from "@vueuse/core";
import { createApiClient } from "#/shared/api/client";

/**
 * Returns the cache key of a scope: its entries sorted by filter name, so the same filters in a
 * different order name one request.
 *
 * @param {Object} [scope]
 * @returns {String}
 */
function scopeKey(scope) {
  return JSON.stringify(Object.entries(scope ?? {}).sort(([a], [b]) => a.localeCompare(b)));
}

function buildClient({ url, readRows, clientOptions }) {
  const instance = createApiClient({ ...clientOptions, baseURL: url, parse: false });

  // Keyed by the scope, so concurrent requests for one scope share a single response and a
  // repeated scope resolves from the cache. A failure is evicted, leaving the next call to retry.
  const fetchRows = useMemoize(async (scope) => readRows(await instance.get("", scope ? { scope } : {})), {
    getKey: scopeKey,
  });

  return {
    /**
     * Returns the option rows the given provider-search filters still match. A scope reaches the
     * endpoint as Rails-nested `scope[<filter>]=<value>` params appended to the URL as given.
     *
     * @param {Object} [params]
     * @param {Object} [params.scope] - Provider-search filters, e.g. `{ skill: "specialty-1" }`.
     * @returns {Promise<Array<Object>>} The rows `readRows` finds, empty when it finds none.
     */
    all: async ({ scope } = {}) => {
      try {
        return await fetchRows(scope);
      } catch (error) {
        fetchRows.delete(scope);

        throw error;
      }
    },
  };
}

const clients = new Map();

/**
 * Returns the data source for one section's scoped option rows: which rows of an endpoint's
 * response a provider-search scope still matches. Every caller reading the same `section` from
 * the same `url` shares one client, and with it one cache.
 *
 * @param {Object} options
 * @param {String} options.url - The endpoint, requested as given with the scope appended.
 * @param {Function} options.readRows - `(payload) => Array<Object>`, unwrapping the raw response
 *   into the rows `all` resolves.
 * @param {String} [options.section] - Distinguishes callers that read different sections of the
 *   same `url` from each other; omit when a client only ever reads one section per `url`.
 * @param {Object} [options.clientOptions] - Extra `ApiClient` constructor options, e.g. `root`.
 * @throws {TypeError} Without a `url`.
 */
export function createScopedRowsClient({ url, readRows, section, clientOptions } = {}) {
  if (!url) {
    throw new TypeError("createScopedRowsClient requires a url");
  }

  const key = section === undefined ? url : `${url}#${section}`;
  const existing = clients.get(key);

  if (existing) {
    return existing;
  }

  const client = buildClient({ url, readRows, clientOptions });
  clients.set(key, client);

  return client;
}
